import { Quota, QuotaGroup, QuotaTargetValue } from '../models/project-quota';

// Reading a quota cell's target has to cope with two storage generations at once, and will do
// indefinitely — there is no data migration and v1 questions stay on the fixed columns.
//
//   v1  → the target lives in the fixed QuotaTargetGroup columns (minAge, region, gender, …)
//   v2+ → the target lives in QuotaTargetValue rows, one per dimension of the cell
//
// These helpers are the only place that branch should be written. Read through them rather than
// touching quota.targetGroup directly, so a cell with no target group at all is handled once.

// Every spelling these dimensions go by across the screens — nameArray uses the question Type
// ('HouseholdIncome'), while the group name is the display label ('Household Income').
const NUMERIC_LEGACY_COLUMNS: Record<string, [string, string]> = {
  'Age': ['minAge', 'maxAge'],
  'HouseholdIncome': ['minHHI', 'maxHHI'],
  'Household Income': ['minHHI', 'maxHHI'],
  'Income': ['minHHI', 'maxHHI'],
  'CompanySize': ['minCompanySize', 'maxCompanySize'],
  'Company Size': ['minCompanySize', 'maxCompanySize'],
};

// A group identifies its dimensions by nameArray where it has one, and by its display name where it
// doesn't (the homepage card builds groups without a nameArray).
function dimensionName(group: QuotaGroup, dimensionIndex: number): string {
  return group?.nameArray?.[dimensionIndex] ?? group?.name ?? '';
}

// The question snapshot a group pins for the given dimension. A nested group's questionId is a
// comma-joined list, one entry per dimension, in the same order as nameArray.
export function pinnedQuestionId(group: QuotaGroup, dimensionIndex = 0): string | null {
  return group?.questionId?.split(',')[dimensionIndex] ?? null;
}

// The generic target row for one dimension of a cell, if the cell is stored generically.
export function targetValueFor(quota: Quota, group: QuotaGroup, dimensionIndex = 0): QuotaTargetValue | null {
  const questionId = pinnedQuestionId(group, dimensionIndex);
  if (!questionId) return null;
  return quota?.targetValues?.find(tv => tv.quotaQuestionId === questionId) ?? null;
}

// A numeric dimension's range. Prefers the generic row; falls back to the fixed column the legacy
// model used for that dimension, which is what a v1 cell still carries.
export function quotaRange(quota: Quota, group: QuotaGroup, dimensionIndex = 0): { min: number | null, max: number | null } {
  const tv = targetValueFor(quota, group, dimensionIndex);
  if (tv && (tv.rangeMin != null || tv.rangeMax != null)) {
    return { min: tv.rangeMin ?? null, max: tv.rangeMax ?? null };
  }
  // 'pnta' is stored as a categorical value even on a numeric dimension; the UI represents it as
  // the -1 sentinel the fixed columns used.
  if (tv?.optionValue === 'pnta') return { min: -1, max: null };

  const columns = NUMERIC_LEGACY_COLUMNS[dimensionName(group, dimensionIndex)];
  if (!columns || !quota?.targetGroup) return { min: null, max: null };
  return {
    min: quota.targetGroup[columns[0]] ?? null,
    max: quota.targetGroup[columns[1]] ?? null,
  };
}

// Find (or create) the generic target row for one dimension of a cell. Callers must have already
// established that the group is generic — see the warning on setQuotaRange.
function upsertTargetValue(quota: Quota, group: QuotaGroup, dimensionIndex: number): QuotaTargetValue | null {
  const questionId = pinnedQuestionId(group, dimensionIndex);
  if (!questionId || !quota) return null;

  quota.targetValues = quota.targetValues ?? [];
  let tv = quota.targetValues.find(t => t.quotaQuestionId === questionId);
  if (!tv) {
    tv = { quotaQuestionId: questionId };
    quota.targetValues.push(tv);
  }
  return tv;
}

// Write a numeric dimension's range to the generic model, so an in-memory edit is visible to code
// reading through quotaRange before the server has re-derived anything.
//
// Only generic groups get target values. Writing one onto a v1 cell would be actively harmful: both
// the recalc proc and the backend save path treat "has QuotaTargetValue rows" as the definition of a
// generic cell, so a v1 cell carrying one would be dropped from the legacy fixed-column match and
// stop counting altogether.
export function setQuotaRange(quota: Quota, group: QuotaGroup, min: number | null, max: number | null, dimensionIndex = 0): void {
  if ((group?.version ?? 1) < 2) return;

  const tv = upsertTargetValue(quota, group, dimensionIndex);
  if (!tv) return;

  if (min === -1) {
    // the prefer-not-to-answer sentinel is categorical on the generic model
    tv.optionValue = 'pnta';
    tv.rangeMin = null;
    tv.rangeMax = null;
  } else {
    tv.optionValue = null;
    tv.rangeMin = min;
    tv.rangeMax = max;
  }
}

// A Region dimension's country-prefixed value ("US: Northeast"), from whichever model holds it.
export function quotaRegion(quota: Quota, group: QuotaGroup, dimensionIndex = 0): string | null {
  const tv = targetValueFor(quota, group, dimensionIndex);
  if (tv?.optionValue != null) return tv.optionValue;
  return quota?.targetGroup?.region ?? null;
}

// Write a Region dimension's value. Region was the last dimension still dual-writing the fixed
// column on a v2 cell — it did so only because the Region UI read targetGroup.region directly in ~34
// places. Those all read through quotaRegion now, so a generic cell keeps its value entirely in
// targetValues like every other categorical dimension, and targetGroup stays strictly v1.
export function setQuotaRegion(quota: Quota, group: QuotaGroup, value: string | null, dimensionIndex = 0): void {
  if ((group?.version ?? 1) < 2) {
    if (quota?.targetGroup) quota.targetGroup.region = value;
    return;
  }

  const tv = upsertTargetValue(quota, group, dimensionIndex);
  if (tv) tv.optionValue = value;
}
